/**
 * The agentic loop (docs/agent.md §3).
 *
 * messages → LLM → tool calls → executors → results → LLM → … until the model
 * stops calling tools, a cap trips, or the run is cancelled.
 *
 * Three rules shape everything here:
 *
 *  1. **Executor failures are results, not exceptions.** A bad argument, a
 *     missing clip, an unreachable archive — all come back as a tool message
 *     the model reads and corrects from. Nothing but a transport failure ends
 *     a run early. This is the core strategy for making cheap models usable.
 *
 *  2. **Ops are never silently dropped.** If a run ends with a non-empty op log
 *     and the model never called `propose`, the loop proposes for it. Work the
 *     user cannot see or apply is worse than an untidy proposal card.
 *
 *  3. **Nothing here touches shared state.** Mutations land on a run-local
 *     draft; the only ways out are the sinks, and the only way onto the real
 *     timeline is a proposal the user applies.
 */
import { produce } from 'immer';
import { LlmTransportError } from './llm';
import { normalizeAllTracks } from '../../format/invariants';
import type { Edit } from '../../format/types';
import { replayOps } from '../proposal/replay';
import {
  isMutate,
  replayRegistry,
  toolSchemas,
  toolsByName,
  validateArgs,
  type AnyToolDef,
  type ArchivePort,
  type PresentToolDef,
  type ReadToolDef,
} from '../tools/catalog';
import type { EmbeddingsPort } from '../tools/embeddingsTypes';
import {
  DEFAULT_LIMITS,
  type AgentChatDraft,
  type AgentEvent,
  type LlmMessage,
  type LlmTransport,
  type LlmUsage,
  type Op,
  type ProposalDraft,
  type RunEndReason,
  type RunLimits,
  type RunSinks,
  type ToolResult,
} from './types';

/**
 * Everything the loop can do, injected. Defined here rather than in types.ts
 * because it references the tool ports, and types.ts must stay importable by
 * the catalog without a cycle.
 */
export interface RunContext {
  runId: string;
  llm: LlmTransport;
  tools: AnyToolDef[];
  port: ArchivePort;
  /** Semantic search, when the run has it (docs/agent.md §9). */
  embeddings?: EmbeddingsPort;
  archives: { key: string; name: string }[];
  /** The live timeline the draft starts from and proposals replay onto. */
  baseEdit: Edit;
  live: {
    selection: string[];
    playhead: number;
    bin: { id: string; title: string; archive?: string; item?: string; in?: number; out?: number }[];
  };
  sinks: RunSinks;
  systemPrompt: string;
  /** Prior conversation, already trimmed by the caller if it wants to. */
  history?: LlmMessage[];
  limits?: Partial<RunLimits>;
  signal?: AbortSignal;
  temperature?: number;
  maxTokens?: number;
}

export interface RunResult {
  reason: RunEndReason;
  text: string;
  usage: LlmUsage;
  ops: Op[];
  proposals: string[];
  messages: LlmMessage[];
}

export async function* runAgentTurn(
  ctx: RunContext,
  userMessage: string,
): AsyncGenerator<AgentEvent, RunResult> {
  const limits: RunLimits = { ...DEFAULT_LIMITS, ...ctx.limits };
  const byName = toolsByName(ctx.tools);
  const schemas = toolSchemas(ctx.tools);
  const deadline = Date.now() + limits.wallClockMs;
  /** Read calls already made this turn → a short summary of what they returned. */
  const seen = new Map<string, string>();

  // Run-local state. `draft` starts as a deep clone: nothing the model does can
  // reach the live edit except through a proposal.
  let draft: Edit = structuredClone(ctx.baseEdit);
  let oplog: Op[] = [];
  const proposals: string[] = [];
  const usage: LlmUsage = { promptTokens: 0, completionTokens: 0 };

  const messages: LlmMessage[] = [
    { role: 'system', content: ctx.systemPrompt },
    ...windowHistory(ctx.history ?? [], limits.historyWindow),
    { role: 'user', content: userMessage },
  ];

  let assistantText = '';
  let toolCallCount = 0;
  // Monotonic across the whole run: the stable identity of a step, since the
  // provider's tool-call ids are not guaranteed unique (types.ts, AgentEvent).
  let stepSeq = 0;
  let reason: RunEndReason = 'complete';

  ctx.sinks.status({ runId: ctx.runId, state: 'running', label: 'Thinking…' });

  try {
    for (let step = 0; step < limits.maxSteps; step++) {
      if (ctx.signal?.aborted) {
        reason = 'cancelled';
        break;
      }
      if (Date.now() > deadline) {
        reason = 'timeout';
        break;
      }

      let stepText = '';
      let calls: { id: string; name: string; rawArgs: string }[] = [];
      let finishReason = 'stop';

      try {
        const stream = ctx.llm(
          {
            messages,
            tools: schemas,
            toolChoice: 'auto',
            maxTokens: ctx.maxTokens,
            temperature: ctx.temperature,
          },
          ctx.signal ?? new AbortController().signal,
        );
        for await (const event of stream) {
          switch (event.type) {
            case 'text-delta':
              stepText += event.text;
              assistantText += event.text;
              yield { type: 'text-delta', text: event.text };
              break;
            case 'tool-calls':
              calls = event.calls.map((c) => ({
                id: c.id,
                name: c.function.name,
                rawArgs: c.function.arguments,
              }));
              break;
            case 'usage':
              usage.promptTokens += event.usage.promptTokens;
              usage.completionTokens += event.usage.completionTokens;
              yield { type: 'usage', usage: event.usage };
              break;
            case 'finish':
              finishReason = event.reason;
              break;
          }
        }
      } catch (err) {
        // Transport failure is the one thing the model cannot correct.
        if (ctx.signal?.aborted) {
          reason = 'cancelled';
          break;
        }
        const message = err instanceof Error ? err.message : String(err);
        const code = err instanceof LlmTransportError ? err.code : undefined;
        yield { type: 'error', message, code };
        reason = 'error';
        break;
      }

      // Re-check cancellation after the stream, not only at the top of the
      // step. A Stop that lands mid-stream would otherwise be read as a normal
      // finish — and a "complete" run auto-proposes its draft, which is exactly
      // what cancelling is meant to prevent.
      if (ctx.signal?.aborted) {
        reason = 'cancelled';
        break;
      }

      // A provider can return HTTP 200 and still report failure in the stream:
      // Gemini emits finish_reason "error" / MALFORMED_FUNCTION_CALL when it
      // cannot produce a valid tool call. Without this, an empty failed turn is
      // indistinguishable from "the model had nothing more to say" — the run
      // reports success, spends nothing, and does nothing, which is the most
      // expensive kind of silent failure to debug.
      if (finishReason === 'error' && !calls.length && !stepText) {
        yield {
          type: 'error',
          message:
            'the model returned an error instead of a reply (finish_reason "error"). ' +
            'This usually means it could not produce a valid tool call for this tool set.',
        };
        reason = 'error';
        break;
      }

      // No tool calls: the model has said its piece and the turn is over.
      if (!calls.length) {
        reason = 'complete';
        break;
      }

      messages.push({
        role: 'assistant',
        content: stepText || null,
        tool_calls: calls.map((c) => ({
          id: c.id,
          type: 'function' as const,
          function: { name: c.name, arguments: c.rawArgs },
        })),
      });

      let hitToolCap = false;
      for (const call of calls) {
        const tool = byName[call.name];
        const label = tool ? safeDescribe(tool, call.rawArgs) : call.name;
        const seq = stepSeq++;

        // A capped call still gets a start/result pair. It used to get neither,
        // which left the trace silently short — and "why did it stop there?" is
        // exactly the question the trace exists to answer.
        if (toolCallCount >= limits.maxToolCalls) {
          hitToolCap = true;
          const capped: ToolResult = {
            ok: false,
            error: 'tool call limit reached for this run — summarise what you have done and stop',
          };
          const cappedMsg = toolMessage(call.id, capped);
          yield {
            type: 'tool-start',
            id: call.id,
            seq,
            tool: call.name,
            kind: tool?.kind ?? 'unknown',
            args: parseArgsLoose(call.rawArgs),
            rawArgs: call.rawArgs,
            label,
          };
          yield {
            type: 'tool-result',
            id: call.id,
            seq,
            tool: call.name,
            result: capped,
            content: cappedMsg.content ?? '',
            ms: 0,
          };
          messages.push(cappedMsg);
          continue;
        }
        toolCallCount += 1;

        yield {
          type: 'tool-start',
          id: call.id,
          seq,
          tool: call.name,
          kind: tool?.kind ?? 'unknown',
          args: parseArgsLoose(call.rawArgs),
          rawArgs: call.rawArgs,
          label,
        };
        ctx.sinks.status({ runId: ctx.runId, state: 'running', label });

        const startedAt = Date.now();
        const dispatched = await dispatch(call, tool, {
          ctx,
          seen,
          draft: () => draft,
          setDraft: (next) => {
            draft = next;
          },
          appendOp: (op) => {
            oplog = [...oplog, op];
          },
          takeOps: () => {
            const taken = oplog;
            oplog = [];
            return taken;
          },
          onProposal: (id) => proposals.push(id),
        });
        const outcome = dispatched.result;

        // Build the tool message once and yield the very same string, so the
        // inspector shows byte-for-byte what the model received — including the
        // resultBudget truncation, which is often the thing worth seeing.
        const msg = toolMessage(call.id, outcome, tool?.resultBudget);
        yield {
          type: 'tool-result',
          id: call.id,
          seq,
          tool: call.name,
          result: outcome,
          content: msg.content ?? '',
          ms: Date.now() - startedAt,
          ...(dispatched.opArgs ? { opArgs: dispatched.opArgs } : {}),
          ...(dispatched.ops ? { ops: dispatched.ops } : {}),
        };
        messages.push(msg);
      }

      if (hitToolCap) {
        reason = 'tool-cap';
        break;
      }
      if (reason !== 'complete') break;

      // Loop back around: the model now sees the tool results.
      if (step === limits.maxSteps - 1) reason = 'step-cap';
    }
  } finally {
    ctx.sinks.status({ runId: ctx.runId, state: 'idle', label: '' });
  }

  // Rule 2: never drop work. A cancelled run is the exception — the user asked
  // for it to stop, so its partial draft is discarded (§1 failure states).
  if (oplog.length && reason !== 'cancelled') {
    const id = publishProposal(ctx, {
      title: 'Unreviewed changes',
      summary:
        'The assistant made these edits but did not finish proposing them' +
        (reason === 'step-cap' || reason === 'tool-cap'
          ? ' before hitting its limit for this run.'
          : '.'),
      ops: oplog,
    });
    if (id) proposals.push(id);
    oplog = [];
  }

  yield { type: 'done', reason };

  return { reason, text: assistantText, usage, ops: oplog, proposals, messages };
}

// ---------------------------------------------------------------------------
// tool dispatch
// ---------------------------------------------------------------------------

/**
 * A key that does not care what order the model emitted its arguments in.
 * `{query, limit}` and `{limit, query}` are the same call and must collide.
 */
function stableArgs(args: Record<string, unknown>): string {
  const walk = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === 'object') {
      return Object.fromEntries(
        Object.entries(v as Record<string, unknown>)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([k, val]) => [k, walk(val)]),
      );
    }
    return v;
  };
  return JSON.stringify(walk(args));
}

/** Enough of a repeated call's result to remind the model why it was useless. */
function summariseResult(result: ToolResult): string {
  const text = JSON.stringify(result);
  return text.length > 200 ? `${text.slice(0, 199)}…` : text;
}

interface DispatchDeps {
  ctx: RunContext;
  draft(): Edit;
  setDraft(edit: Edit): void;
  appendOp(op: Op): void;
  takeOps(): Op[];
  onProposal(id: string): void;
  /** READ calls already made this run, keyed by tool + normalized args. */
  seen: Map<string, string>;
}

/**
 * What a dispatch produced, beyond the result the model sees.
 *
 * `opArgs` is reported even when `apply` then failed: the resolved arguments are
 * precisely what you need to see in order to understand why it failed, and they
 * used to be discarded on exactly that path.
 */
interface Dispatched {
  result: ToolResult;
  opArgs?: Record<string, unknown>;
  ops?: Op[];
}

async function dispatch(
  call: { id: string; name: string; rawArgs: string },
  tool: AnyToolDef | undefined,
  deps: DispatchDeps,
): Promise<Dispatched> {
  if (!tool) {
    const known = deps.ctx.tools.map((t) => t.name).join(', ');
    return {
      result: { ok: false, error: `no tool named "${call.name}". Available tools: ${known}` },
    };
  }

  let parsed: unknown;
  try {
    parsed = call.rawArgs.trim() ? JSON.parse(call.rawArgs) : {};
  } catch {
    return {
      result: {
        ok: false,
        error: `arguments for ${call.name} were not valid JSON. Send a single JSON object.`,
      },
    };
  }

  const validated = validateArgs(tool.schema, parsed);
  if (!validated.ok) return { result: { ok: false, error: validated.error } };
  const args = validated.value;

  // A model that gets an unhelpful search result will often just run it again.
  // Observed on a real run: eight identical `search_items {query:"Guddi"}`
  // calls, 56k tokens, no answer — the archive dutifully returned the same
  // rows every time and nothing in the transcript said "you have been here
  // before". Repeating a READ is always pointless within one turn: the archive
  // has not changed and neither have the arguments. Mutations are excluded
  // because repeating one is a legitimate edit (two identical clips in a row).
  if (tool.kind === 'read') {
    const key = `${call.name}:${stableArgs(args)}`;
    const previous = deps.seen.get(key);
    if (previous !== undefined) {
      return {
        result: {
          ok: false,
          error:
            `you already called ${call.name} with exactly these arguments in this turn and got: ` +
            `${previous}. Running it again will return the same thing — change the arguments ` +
            `(different words, a different field, a wider limit) or use what you have.`,
        },
      };
    }
    deps.seen.set(key, '');
  }

  // Captured outside the try so every exit path below can report them.
  let opArgs: Record<string, unknown> | undefined;
  let tookOps: Op[] | undefined;

  try {
    if (tool.kind === 'read') {
      const result = await (tool as ReadToolDef).run(args, {
        draft: deps.draft,
        live: deps.ctx.live,
        archives: deps.ctx.archives,
        port: deps.ctx.port,
        ...(deps.ctx.embeddings ? { embeddings: deps.ctx.embeddings } : {}),
        // search_moments posts its own results: the user sees every match
        // while the model sees a digest (tools/catalog.ts, ReadContext).
        postChat: (msg) => deps.ctx.sinks.postChat(msg as AgentChatDraft),
      });
      deps.seen.set(`${call.name}:${stableArgs(args)}`, summariseResult(result));
      return { result };
    }

    if (isMutate(tool)) {
      const resolved = await tool.resolve(args, {
        getItem: async (archive, item) => {
          const detail = await deps.ctx.port.getItem(archive, item);
          return {
            id: detail.id,
            title: detail.title,
            durations: detail.durations ?? [],
            duration: detail.duration ?? 0,
            posterFrame: detail.posterFrame ?? 0,
          };
        },
        draft: deps.draft,
        live: { playhead: deps.ctx.live.playhead, selection: deps.ctx.live.selection },
      });
      if (!resolved.ok) return { result: { ok: false, error: resolved.error } };
      opArgs = resolved.opArgs as Record<string, unknown>;

      let applyError: string | null = null;
      let produced: string[] = [];
      const next = produce(deps.draft(), (d) => {
        const result = tool.apply(d, resolved.opArgs);
        if ('error' in result) {
          applyError = result.error;
          return;
        }
        produced = result.created;
        normalizeAllTracks(d.tracks);
      });
      if (applyError) return { result: { ok: false, error: applyError }, opArgs };

      deps.setDraft(next);
      deps.appendOp({ tool: tool.name, args: resolved.opArgs, produced });
      return {
        result: { ok: true, created: produced, timeline: summarise(next) },
        opArgs,
      };
    }

    // present / control
    const result = await (tool as PresentToolDef).run(args, {
      postChat: (msg) => deps.ctx.sinks.postChat(msg as AgentChatDraft),
      proposeOps: (title, summary) => {
        const ops = deps.takeOps();
        tookOps = ops;
        if (!ops.length) return '';
        const id = publishProposal(deps.ctx, { title, summary, ops });
        if (id) deps.onProposal(id);
        return id;
      },
    });
    return { result, ...(tookOps ? { ops: tookOps } : {}) };
  } catch (err) {
    // An executor that throws is a bug, not a model error — but killing the run
    // helps nobody, so it comes back as a result the model can route around.
    return {
      result: {
        ok: false,
        error: `${call.name} failed: ${err instanceof Error ? err.message : String(err)}`,
      },
      ...(opArgs ? { opArgs } : {}),
      ...(tookOps ? { ops: tookOps } : {}),
    };
  }
}

function publishProposal(ctx: RunContext, draft: ProposalDraft): string {
  return ctx.sinks.publishProposal(draft);
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/**
 * Trim old turns, but never split an assistant tool_calls message from the tool
 * messages answering it — providers reject a tool message whose call is missing,
 * so a naive slice can make every subsequent request fail.
 */
export function windowHistory(history: LlmMessage[], limit: number): LlmMessage[] {
  if (history.length <= limit) return history;
  let start = history.length - limit;
  while (start < history.length && history[start].role === 'tool') start += 1;
  return history.slice(start);
}

function toolMessage(id: string, result: ToolResult, budget?: number): LlmMessage {
  let content = JSON.stringify(result);
  const cap = budget ?? 2000;
  if (content.length > cap) {
    content = `${content.slice(0, cap)}… [truncated; narrow your request to see more]`;
  }
  return { role: 'tool', tool_call_id: id, content };
}

/** One-line state summary returned after every mutation, so the model can see. */
function summarise(edit: Edit): string {
  const parts = edit.tracks
    .filter((t) => (t.clips as unknown[]).length)
    .map((t) => `${t.label}: ${(t.clips as unknown[]).length} clip(s)`);
  const total = edit.tracks.reduce(
    (max, t) =>
      Math.max(
        max,
        ...(t.clips as { start: number }[]).map((c) => c.start),
        0,
      ),
    0,
  );
  return parts.length
    ? `${parts.join(', ')}; last clip starts at ${Math.round(total * 10) / 10}s`
    : 'timeline is empty';
}

function parseArgsLoose(raw: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(raw || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/** describeCall runs on unvalidated args, so it must never be able to throw. */
function safeDescribe(tool: AnyToolDef, rawArgs: string): string {
  try {
    return tool.describeCall(parseArgsLoose(rawArgs));
  } catch {
    return tool.name;
  }
}

/**
 * Apply a finished proposal's ops onto an edit — the pure half of what the
 * store's applyAgentProposal will do in Phase 4.
 */
export function applyProposalOps(
  base: Edit,
  ops: Op[],
  tools: AnyToolDef[],
): { ok: true; edit: Edit } | { ok: false; conflict: string } {
  const result = replayOps(base, ops, replayRegistry(tools));
  if (!result.ok) return { ok: false, conflict: result.conflict };
  return { ok: true, edit: result.edit };
}
